import {PropsWithChildren, useMemo, useState} from 'react'
import AsyncSelect from 'react-select/async'
import {HeaderProps} from 'react-table'
import Select from 'react-select'
import {components} from 'react-select'
import clsx from 'clsx'
import {useKTable} from '../../../contexts/KTableProvider'
import {useKhipu} from '../../../hooks/useKhipu'
import {KTSVG} from '../../../../../_metronic/helpers'
import {getSelectStyles} from './getSelectStyles'
type Position = 'left' | 'right' | 'center'
type SizeClass = 'small' | 'medium' | 'large'
interface Props<T extends object> {
  className?: string
  title?: string
  sizeClass?: SizeClass
  tableProps: PropsWithChildren<HeaderProps<T>>
  wrapTitle?: boolean
  searcheable?: boolean
  showSelect?: boolean
  showSelectLav?: boolean
  selecteable?: boolean
  multiSelecteable?: boolean
  multiSelectValues?: string[]
  onMultiSelectValuesChange?: (values: string[]) => void
  selecteableAsync?: boolean
  dateable?: boolean
  data?: any
  name?: string
  selectLabel?: string
  size?: string
  number?: boolean
  numberGuion?: boolean
  position?: Position
  fromDetail?: boolean
  placeholder?: string
  valueSelectAsync?: any
  loadOptions?: (inputValue: string) => void
  onGetValueSelect?: (e: Event, name: string) => void
}

/** Valor sintético para la fila "Seleccionar todos" (no se persiste en el estado). */
const MULTI_SELECT_ALL_VALUE = '__KHIPU_SELECT_ALL__'

const MultiOption = (props: any) => {
  const options = props.selectProps?.options ?? []
  const allIds = options
    .filter((o: {value: string}) => o.value !== MULTI_SELECT_ALL_VALUE)
    .map((o: {value: string}) => o.value)
  const selected = props.selectProps?.value
  const selectedValues = Array.isArray(selected)
    ? selected.map((o: {value: string}) => o.value).filter((v: string) => v !== MULTI_SELECT_ALL_VALUE)
    : []
  const allSelected =
    allIds.length > 0 && allIds.every((id: string) => selectedValues.includes(id))
  const isSelectAllRow = props.data?.value === MULTI_SELECT_ALL_VALUE
  const checked = isSelectAllRow ? allSelected : props.isSelected

  return (
    <components.Option {...props}>
      <div className='d-flex align-items-center gap-2'>
        <input type='checkbox' checked={checked} readOnly style={{pointerEvents: 'none'}} />
        <span>{props.label}</span>
      </div>
    </components.Option>
  )
}

const KTableHeader = <T extends object>({
  className,
  data,
  name = '',
  title,
  sizeClass = 'large',
  wrapTitle = false,
  tableProps,
  searcheable,
  selecteable,
  multiSelecteable,
  multiSelectValues,
  onMultiSelectValuesChange,
  selecteableAsync,
  selectLabel = '',
  dateable,
  showSelect,
  showSelectLav,
  size,
  number,
  numberGuion,
  position = 'left',
  fromDetail = false,
  placeholder = 'Busca una opcion...',
  valueSelectAsync,
  loadOptions,
  onGetValueSelect,
}: Props<T>) => {
  const id = tableProps.column.id

  const {isAllSelected, onSelectAll, onSelectAllLav, isAllSelectedLav} = useKTable()
  const {searchValues, changeSearchValues, changeSearchValuesDetail, searchValuesDetail} =
    useKhipu()
  const [selectedOption, setSelectedOption] = useState<Record<string, string>>(searchValues)

  const multiOptions = useMemo(() => {
    if (!multiSelecteable || !Array.isArray(data) || data.length === 0) {
      return data
    }
    return [{value: MULTI_SELECT_ALL_VALUE, label: 'Seleccionar todos'}, ...data]
  }, [multiSelecteable, data])
  const handleFieldChange = (fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    let obj
    if (fromDetail) {
      obj = {
        ...searchValuesDetail,
        [fieldName]: value,
      }
    } else {
      obj = {
        ...searchValues,
        [fieldName]: value,
      }
    }
    setSelectedOption({...selectedOption, [fieldName]: value})
    if (fromDetail) {
      changeSearchValuesDetail(obj)
    } else {
      changeSearchValues(obj)
    }
  }
  const handleSelectChange = (e: any, name: string) => {
    let value = e.value
    let fieldName = name
    let obj
    if (fromDetail) {
      obj = {
        ...searchValuesDetail,
        [fieldName]: value,
      }
    } else {
      obj = {
        ...searchValues,
        [fieldName]: value,
      }
    }
    setSelectedOption({...selectedOption, [fieldName]: value})
    if (fromDetail) {
      changeSearchValuesDetail(obj)
    } else {
      changeSearchValues(obj)
    }
    onGetValueSelect?.(e, name)
  }
  const handleMultiSelectChange = (e: any, name: string) => {
    const selectedOptions = Array.isArray(e) ? e : []
    const rawValues = selectedOptions.map((item: any) => item.value)
    const allIds = (data || [])
      .map((c: {value: string}) => c.value)
      .filter((v) => v != null && v !== '')

    let values: string[]
    if (rawValues.includes(MULTI_SELECT_ALL_VALUE)) {
      const withoutAll = rawValues.filter((v) => v !== MULTI_SELECT_ALL_VALUE)
      const everyModuleSelected =
        allIds.length > 0 &&
        withoutAll.length === allIds.length &&
        allIds.every((id) => withoutAll.includes(id))
      values = everyModuleSelected ? [] : [...allIds]
    } else {
      values = rawValues
    }

    if (onMultiSelectValuesChange) {
      onMultiSelectValuesChange(values)
      onGetValueSelect?.(e, name)
      return
    }
    const fieldName = name
    const currentValues = fromDetail ? searchValuesDetail : searchValues
    const obj = {...currentValues}

    if (values.length > 0) {
      obj[fieldName] = values
    } else {
      delete obj[fieldName]
    }

    setSelectedOption({...selectedOption, [fieldName]: values as any})
    if (fromDetail) {
      changeSearchValuesDetail(obj)
    } else {
      changeSearchValues(obj)
    }
    onGetValueSelect?.(e, name)
  }

  const handleSelectAsyncChange = (e: any, name: string, selectLabel: string) => {
    let value = e.value
    let label = e.label
    let fieldName = name
    let labelName = selectLabel
    let obj
    if (fromDetail) {
      obj = {
        ...searchValuesDetail,
        [fieldName]: value,
        [labelName]: label,
      }
    } else {
      obj = {
        ...searchValues,
        [fieldName]: value,
        [labelName]: label,
      }
    }
    setSelectedOption({...selectedOption, [fieldName]: value, [labelName]: label})
    if (fromDetail) {
      changeSearchValuesDetail(obj)
    } else {
      changeSearchValues(obj)
    }
    onGetValueSelect?.(e, name)
  }
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // let regex = /^\d$/
    // if (!regex.test(e.key) && e.keyCode !== 8) {
    //   e.preventDefault()
    // }
    if (!/^[\d-]$/.test(e.key) && e.key !== 'Backspace') {
      e.preventDefault()
    }
  }
  const handleKeyPressG = (e: React.KeyboardEvent<HTMLInputElement>) => {
    let regex = /^[0-9-]$/
    if (!regex.test(e.key) && e.keyCode !== 8) {
      e.preventDefault()
    }
  }
  if (id === 'selection' && showSelect) {
    return (
      <th
        {...tableProps.column.getHeaderProps()}
        className='ps-2'
        style={{
          width: (tableProps.column as any).width || 32,
          minWidth: (tableProps.column as any).minWidth || 28,
          maxWidth: (tableProps.column as any).maxWidth || 40,
        }}
      >
        <div className='form-check form-check-sm form-check-custom form-check-solid me-3'>
          <input
            className='form-check-input border border-gray-300'
            type='checkbox'
            data-kt-check={isAllSelected}
            data-kt-check-target='#kt_table_users .form-check-input'
            checked={isAllSelected}
            onChange={onSelectAll}
          />
        </div>
      </th>
    )
  }
  if (id === 'selection' && showSelectLav) {
    return (
      <th
        {...tableProps.column.getHeaderProps()}
        className='ps-2'
        style={{
          width: (tableProps.column as any).width || 32,
          minWidth: (tableProps.column as any).minWidth || 28,
          maxWidth: (tableProps.column as any).maxWidth || 40,
        }}
      >
        <div className='form-check form-check-sm form-check-custom form-check-solid me-3'>
          <input
            className='form-check-input border border-gray-300'
            type='checkbox'
            data-kt-check={isAllSelectedLav}
            data-kt-check-target='#kt_table_users .form-check-input'
            checked={isAllSelectedLav}
            onChange={onSelectAllLav}
          />
        </div>
      </th>
    )
  }
  return (
    <>
      <th {...tableProps.column.getHeaderProps()} className={clsx(className)}>
        <div
          style={{
            minHeight: '20px',
            width: '100%',
            maxWidth: '100%',
            whiteSpace: wrapTitle ? ('normal' as const) : ('nowrap' as const),
            overflow: wrapTitle ? ('hidden' as const) : ('hidden' as const),
            textOverflow: wrapTitle ? ('clip' as const) : ('ellipsis' as const),
            display: 'flex',
            justifyContent: position,
            fontSize: sizeClass === 'small' ? '12px' : sizeClass === 'medium' ? '14px' : '0.95rem',
            lineHeight: 1.2,
            height: wrapTitle ? '2.4em' : '1.2em',
          }}
        >
          {title}
        </div>

        {searcheable ? (
          <div style={{minHeight: '35px'}}>
            <div
              className='d-flex align-items-center position-relative'
              style={{width: '100%', minWidth: 0}}
            >
              <div
                className='d-flex align-items-center position-relative'
                style={{width: '100%', minWidth: 0}}
              >
                <KTSVG
                  path='/media/icons/duotune/general/gen021.svg'
                  className={clsx('position-absolute ms-1 text-gray-300', {
                    'svg-icon-6': sizeClass === 'small',
                    'svg-icon-3': sizeClass === 'medium',
                    'svg-icon-1': sizeClass === 'large',
                  })}
                />

                {number ? (
                  fromDetail ? (
                    <input
                      className={clsx(
                        'form-control form-control-solid bg-white border border-gray-300',
                        {
                          'form-control-small ps-6': sizeClass === 'small',
                          'form-control-medium ps-6': sizeClass === 'medium',
                          'form-control-large ps-9 pe-2 py-2': sizeClass === 'large',
                        },
                      )}
                      style={{width: '100%', minWidth: 0, boxSizing: 'border-box'}}
                      onKeyPress={handleKeyPress}
                      onChange={handleFieldChange(name)}
                      name={name}
                      value={searchValuesDetail[name] || ''}
                    />
                  ) : (
                    <input
                      className={clsx(
                        'form-control form-control-solid bg-white border border-gray-300',
                        {
                          'form-control-small ps-6': sizeClass === 'small',
                          'form-control-medium ps-6': sizeClass === 'medium',
                          'form-control-large ps-9 pe-2 py-2': sizeClass === 'large',
                        },
                      )}
                      style={{width: '100%', minWidth: 0, boxSizing: 'border-box'}}
                      onKeyPress={handleKeyPress}
                      onChange={handleFieldChange(name)}
                      name={name}
                      value={searchValues[name] || ''}
                    />
                  )
                ) : numberGuion ? (
                  <input
                    className={clsx(
                      'form-control form-control-solid bg-white border border-gray-300',
                      {
                        'form-control-small ps-6': sizeClass === 'small',
                        'form-control-medium ps-6': sizeClass === 'medium',
                        'form-control-large ps-9 pe-2 py-2': sizeClass === 'large',
                      },
                    )}
                    style={{width: '100%', minWidth: 0, boxSizing: 'border-box'}}
                    onKeyPress={handleKeyPressG}
                    onChange={handleFieldChange(name)}
                    name={name}
                    value={searchValues[name] || ''}
                  />
                ) : fromDetail ? (
                  <input
                    className={clsx(
                      'form-control form-control-solid bg-white border border-gray-300',
                      {
                        'form-control-small ps-6': sizeClass === 'small',
                        'form-control-medium ps-6': sizeClass === 'medium',
                        'form-control-large ps-9 pe-2 py-2': sizeClass === 'large',
                      },
                    )}
                    style={{width: '100%', minWidth: 0, boxSizing: 'border-box'}}
                    name={name}
                    value={searchValuesDetail[name] || ''}
                    onChange={handleFieldChange(name)}
                  />
                ) : (
                  <input
                    className={clsx(
                      'form-control form-control-solid bg-white border border-gray-300',
                      {
                        'form-control-small ps-6': sizeClass === 'small',
                        'form-control-medium ps-6': sizeClass === 'medium',
                        'form-control-large ps-9 pe-2 py-2': sizeClass === 'large',
                      },
                    )}
                    style={{width: '100%', minWidth: 0, boxSizing: 'border-box'}}
                    name={name}
                    value={searchValues[name] || ''}
                    onChange={handleFieldChange(name)}
                  />
                )}
              </div>
            </div>
          </div>
        ) : null}
        {selecteable ? (
          fromDetail ? (
            <div style={{minHeight: '35px'}}>
              <div
                className='d-flex align-items-center position-relative'
                style={{width: '100%', minWidth: 0}}
              >
                <Select
                  key={name}
                  className='react-select-container bg-white border border-gray-300'
                  styles={getSelectStyles(sizeClass, size)}
                  classNamePrefix='react-select'
                  placeholder='Selecciona una opción'
                  options={data}
                  // styles={style}
                  noOptionsMessage={() => 'No hay resultados'}
                  // value={selectedOption[name] || null}
                  value={
                    searchValuesDetail[name]
                      ? data?.find((c: {value: string}) => c.value === searchValuesDetail[name])
                      : null
                  }
                  onChange={(e) => handleSelectChange(e, name)}
                  isSearchable={true}
                />
              </div>
            </div>
          ) : (
            <div style={{minHeight: '35px'}}>
              <div
                className='d-flex align-items-center position-relative'
                style={{width: '100%', minWidth: 0}}
              >
                <Select
                  key={name}
                  className='react-select-container bg-white border border-gray-300'
                  styles={getSelectStyles(sizeClass, size)}
                  classNamePrefix='react-select'
                  placeholder='Selecciona una opción'
                  options={data}
                  // styles={style}
                  noOptionsMessage={() => 'No hay resultados'}
                  // value={selectedOption[name] || null}
                  value={
                    searchValues[name]
                      ? data?.find((c: {value: string}) => c.value === searchValues[name])
                      : null
                  }
                  onChange={(e) => handleSelectChange(e, name)}
                  isSearchable={true}
                />
              </div>
            </div>
          )
        ) : null}
        {multiSelecteable ? (
          fromDetail ? (
            <div style={{minHeight: '35px'}}>
              <div
                className='d-flex align-items-center position-relative'
                style={{width: '100%', minWidth: 0}}
              >
                <Select
                  key={name}
                  isMulti
                  closeMenuOnSelect={false}
                  blurInputOnSelect={false}
                  hideSelectedOptions={false}
                  controlShouldRenderValue={false}
                  components={{Option: MultiOption}}
                  className='react-select-container bg-white border border-gray-300'
                  styles={getSelectStyles(sizeClass, size)}
                  classNamePrefix='react-select'
                  placeholder='Selecciona una o más opciones'
                  options={multiOptions}
                  filterOption={(option, inputValue) => {
                    if ((option.data as {value?: string})?.value === MULTI_SELECT_ALL_VALUE) {
                      return true
                    }
                    return String(option.label ?? '')
                      .toLowerCase()
                      .includes((inputValue || '').toLowerCase())
                  }}
                  noOptionsMessage={() => 'No hay resultados'}
                  value={
                    Array.isArray(multiSelectValues)
                      ? data?.filter((c: {value: string}) => multiSelectValues.includes(c.value))
                      : Array.isArray(searchValuesDetail[name])
                        ? data?.filter((c: {value: string}) => searchValuesDetail[name].includes(c.value))
                      : []
                  }
                  onChange={(e) => handleMultiSelectChange(e, name)}
                  isSearchable={true}
                />
              </div>
            </div>
          ) : (
            <div style={{minHeight: '35px'}}>
              <div
                className='d-flex align-items-center position-relative'
                style={{width: '100%', minWidth: 0}}
              >
                <Select
                  key={name}
                  isMulti
                  closeMenuOnSelect={false}
                  blurInputOnSelect={false}
                  hideSelectedOptions={false}
                  controlShouldRenderValue={false}
                  components={{Option: MultiOption}}
                  className='react-select-container bg-white border border-gray-300'
                  styles={getSelectStyles(sizeClass, size)}
                  classNamePrefix='react-select'
                  placeholder='Selecciona una o más opciones'
                  options={multiOptions}
                  filterOption={(option, inputValue) => {
                    if ((option.data as {value?: string})?.value === MULTI_SELECT_ALL_VALUE) {
                      return true
                    }
                    return String(option.label ?? '')
                      .toLowerCase()
                      .includes((inputValue || '').toLowerCase())
                  }}
                  noOptionsMessage={() => 'No hay resultados'}
                  value={
                    Array.isArray(multiSelectValues)
                      ? data?.filter((c: {value: string}) => multiSelectValues.includes(c.value))
                      : Array.isArray(searchValues[name])
                        ? data?.filter((c: {value: string}) => searchValues[name].includes(c.value))
                      : []
                  }
                  onChange={(e) => handleMultiSelectChange(e, name)}
                  isSearchable={true}
                />
              </div>
            </div>
          )
        ) : null}
        {selecteableAsync ? (
          fromDetail ? (
            <div style={{minHeight: '35px'}}>
              <div
                className='d-flex align-items-center position-relative'
                style={{width: '100%', minWidth: 0}}
              >
                <AsyncSelect
                  key={name}
                  // cacheOptions
                  defaultOptions
                  loadOptions={loadOptions as any}
                  className='react-select-container bg-white border border-gray-300'
                  styles={getSelectStyles(sizeClass, size)}
                  classNamePrefix='react-select'
                  noOptionsMessage={() => 'No hay resultados'}
                  menuPortalTarget={document.body}
                  placeholder={placeholder}
                  // styles={{...customStyles}}
                  value={
                    searchValuesDetail[name]
                      ? {value: searchValuesDetail[name], label: searchValuesDetail[selectLabel]}
                      : null
                  }
                  onChange={(e) => handleSelectAsyncChange(e, name, selectLabel)}
                />
              </div>
            </div>
          ) : (
            <div style={{minHeight: '35px'}}>
              <div
                className='d-flex align-items-center position-relative'
                style={{width: '100%', minWidth: 0}}
              >
                <AsyncSelect
                  key={name}
                  // cacheOptions
                  defaultOptions
                  loadOptions={loadOptions as any}
                  className='react-select-container bg-white border border-gray-300'
                  styles={getSelectStyles(sizeClass, size)}
                  classNamePrefix='react-select'
                  noOptionsMessage={() => 'No hay resultados'}
                  menuPortalTarget={document.body}
                  placeholder={placeholder}
                  // styles={{...customStyles}}
                  value={
                    searchValues[name]
                      ? {value: searchValues[name], label: searchValues[selectLabel]}
                      : null
                  }
                  onChange={(e) => handleSelectAsyncChange(e, name, selectLabel)}
                />
              </div>
            </div>
          )
        ) : null}

        {dateable ? (
          fromDetail ? (
            <div style={{minHeight: '35px'}}>
              <div
                className='d-flex align-items-center position-relative'
                style={{width: '100%', minWidth: 0, overflow: 'hidden'}}
              >
                <input
                  type='Date'
                  className={clsx(
                    'form-control form-control-solid bg-white border border-gray-300',
                  )}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    paddingLeft: '0.3rem',
                    paddingRight: '0.4rem',
                    fontSize: '12px',
                    display: 'block',
                  }}
                  name={name}
                  value={searchValuesDetail[name] || ''}
                  onChange={handleFieldChange(name)}
                />
              </div>
            </div>
          ) : (
            <div style={{minHeight: '35px'}}>
              <div
                className='d-flex align-items-center position-relative'
                style={{width: '100%', minWidth: 0, overflow: 'hidden'}}
              >
                <input
                  type='Date'
                  className={clsx(
                    'form-control form-control-solid bg-white border border-gray-300',
                  )}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    paddingLeft: '0.3rem',
                    paddingRight: '0.4rem',
                    fontSize: '12px',
                    display: 'block',
                  }}
                  name={name}
                  value={searchValues[name] || ''}
                  onChange={handleFieldChange(name)}
                />
              </div>
            </div>
          )
        ) : null}
      </th>
    </>
  )
}

export {KTableHeader}
